import React, { useRef, useEffect, useState, forwardRef, RefObject } from 'react';

interface ScrollableContainerProps extends  Omit<React.HTMLAttributes<HTMLDivElement>, 'onScroll' | 'ref'> {
  onBottomReached?: () => void;
  onTopReached?: () => void;
  children: React.ReactElement
  dataLength: number,
}

const ScrollableContainer = forwardRef<HTMLDivElement, ScrollableContainerProps>(({
    children, 
    onTopReached,
    onBottomReached,
    dataLength,
    style = { overflowY: 'auto' },
    ...props
  
}, ref) => {

  // internalRef in case parent doesn't provide ref
  const internalRef = useRef<HTMLDivElement>(null);

  // containerRef
  // either ref provided by parent or internal ref
  // we only use object refs callback refs are unsupported
  const containerRef = ref && typeof ref === 'object'
    ? ref
    : internalRef;

  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);

  // reset scroll position when new elements are added to the list
  useEffect(() => {

    if (containerRef.current) {

      containerRef.current.scrollBy({
        top: containerRef.current.scrollHeight - previousScrollHeight,
      })

    }
    handleScroll();
  }, [dataLength]);
  
  // on mount
  // - scroll to bottom
  // - trigger handle scroll
  useEffect(() => {

    if (containerRef.current) {

      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      handleScroll();

    };
  }, []);

  useEffect(() => {
    handleScroll();
  }, [])


  const handleScroll = () => {
    if (containerRef.current) {

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

      setPreviousScrollHeight(containerRef.current.scrollHeight);

      // reached top
      if (scrollTop === 0) {

        if (onTopReached !== undefined) {
          onTopReached();
        }
      }  
  
      // reached bottom
      
      // error margin
      const errorMargin = 5; 

      // sometimes scrollTop + clientHeight is slightly less than scrollHeight
      // so we subsctract errorMargin from scroll height and compare with it.

      if (scrollTop + clientHeight >= scrollHeight - errorMargin) {
        if (onBottomReached !== undefined) {
          onBottomReached();
        }

      }
    }
  };

  // Call scroll function on click
  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    containerRef.current.addEventListener('click', handleScroll);

    // Cleanup the event listener
    return () => {
      containerRef.current?.removeEventListener('click', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={style}
      {...props}
    >
      { children }
    </div>
  );
})


export default ScrollableContainer;
